/**
 * Team — who can see this account's dashboard.
 *
 * Visible to owners and admins only. The rail hides it for everyone else, but
 * that is presentation: every route behind this screen re-checks the role on the
 * server, so a hand-typed URL gains nothing.
 *
 * Errors are shown in the words the server used. An access rule the customer
 * cannot understand is an access rule they will phone us about.
 */
import { useCallback, useEffect, useState } from 'react';
import { border, c, mono, radius } from '../theme/tokens';
import { Card, CardHead, Eyebrow, H1, Mono, Pill } from '../components/primitives';
import { Failure, LoadingPanel } from '../components/states';
import { api, ApiError } from '../api/client';
import type { DashboardRole, TeamResult } from '../api/types';

const ROLE_COPY: Record<DashboardRole, string> = {
  owner: 'Full access, including billing and voice settings.',
  admin: 'Sees every call, and can invite or remove colleagues.',
  member: 'Sees every call. Cannot manage team access.',
};

function roleTone(role: DashboardRole) {
  if (role === 'owner') return { fg: c.indigo, bg: c.indigo100 };
  if (role === 'admin') return { fg: c.orange700, bg: c.orange100 };
  return { fg: c.ink3, bg: c.lineSoft };
}

function formatDate(value: string | null | undefined) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function TextButton({
  children,
  onClick,
  disabled,
  tone = 'default',
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'default' | 'danger';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        fontFamily: mono,
        fontSize: 10,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        background: 'transparent',
        border: 'none',
        padding: '6px 2px',
        cursor: disabled ? 'default' : 'pointer',
        color: disabled ? c.ink3 : tone === 'danger' ? c.danger : c.indigo,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        padding: '14px 0',
        borderTop: `1px solid ${c.lineSoft}`,
        flexWrap: 'wrap',
      }}
    >
      {children}
    </div>
  );
}

export function Team({ tenantName }: { tenantName: string }) {
  const [data, setData] = useState<TeamResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<DashboardRole>('member');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.team();
      setData(result);
      // Default to the least privileged role the actor may actually grant.
      setRole(result.grantableRoles.includes('member') ? 'member' : result.grantableRoles[0]);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load team access.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      const sent = await api.invite(email.trim(), role);
      setNotice(`Invitation sent to ${sent.email}. It expires in 7 days.`);
      setEmail('');
      await load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'The invitation could not be sent.');
    } finally {
      setSubmitting(false);
    }
  }

  async function act(fn: () => Promise<unknown>, failure: string) {
    setFormError(null);
    setNotice(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : failure);
    }
  }

  const domains = data?.domains ?? [];

  return (
    <div
      style={{
        padding: '44px 40px 64px 40px',
        maxWidth: 900,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 28,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Eyebrow>ADMIN · TEAM ACCESS</Eyebrow>
        <H1>Who can see {tenantName}</H1>
        <p style={{ margin: 0, color: c.ink3, fontSize: 14, lineHeight: 1.55, maxWidth: 620 }}>
          Everyone here sees this account's calls and nothing else. Access is by invitation only —
          people cannot sign themselves up.
        </p>
      </div>

      {error ? <Failure message={error} onRetry={() => void load()} /> : null}
      {loading && !data ? <LoadingPanel label="LOADING TEAM" /> : null}

      {data ? (
        <>
          <Card>
            <CardHead title="Invite someone" />
            {domains.length ? (
              <Mono size={10.5}>
                {data.actor.role === 'owner'
                  ? `ANY ADDRESS · APPROVED FOR THIS ACCOUNT: ${domains.join(', ').toUpperCase()}`
                  : `APPROVED DOMAINS: ${domains.join(', ').toUpperCase()}`}
              </Mono>
            ) : null}

            <form onSubmit={invite} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="name@yourcompany.co.uk"
                  style={{
                    flex: '1 1 260px',
                    minWidth: 0,
                    padding: '11px 13px',
                    fontSize: 14,
                    color: c.ink,
                    background: c.surface2,
                    border: border.default,
                    borderRadius: radius.card,
                    fontFamily: 'inherit',
                  }}
                />
                <select
                  value={role}
                  onChange={e => setRole(e.target.value as DashboardRole)}
                  style={{
                    padding: '11px 13px',
                    fontSize: 14,
                    color: c.ink,
                    background: c.surface2,
                    border: border.default,
                    borderRadius: radius.card,
                    fontFamily: 'inherit',
                  }}
                >
                  {data.grantableRoles.map(r => (
                    <option key={r} value={r}>
                      {r === 'owner' ? 'Owner' : r === 'admin' ? 'Admin' : 'Team member'}
                    </option>
                  ))}
                </select>
                <button
                  type="submit"
                  disabled={submitting || !email.trim()}
                  style={{
                    padding: '11px 22px',
                    fontFamily: mono,
                    fontSize: 11,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: c.surface2,
                    background: submitting || !email.trim() ? c.ink3 : c.indigo,
                    border: 'none',
                    borderRadius: radius.pill,
                    cursor: submitting || !email.trim() ? 'default' : 'pointer',
                  }}
                >
                  {submitting ? 'Sending…' : 'Send invitation'}
                </button>
              </div>

              <Mono size={10.5} color={c.ink3}>
                {ROLE_COPY[role].toUpperCase()}
              </Mono>

              {formError ? (
                <div style={{ color: c.danger, fontSize: 13.5, lineHeight: 1.5 }}>{formError}</div>
              ) : null}
              {notice ? (
                <div style={{ color: c.success, fontSize: 13.5, lineHeight: 1.5 }}>{notice}</div>
              ) : null}
            </form>
          </Card>

          <Card>
            <CardHead
              title="Has access"
              right={<Mono size={10.5}>{`${data.users.length} ${data.users.length === 1 ? 'PERSON' : 'PEOPLE'}`}</Mono>}
            />
            {data.users.map(u => {
              const tone = roleTone(u.role);
              return (
                <Row key={u.id}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                    <div style={{ fontSize: 14.5, color: c.ink, overflowWrap: 'anywhere' }}>
                      {u.email ?? 'Address not recorded'}
                      {u.isSelf ? <span style={{ color: c.ink3 }}> · you</span> : null}
                    </div>
                    <Mono size={10}>ADDED {formatDate(u.createdAt).toUpperCase()}</Mono>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <Pill fg={tone.fg} bg={tone.bg}>
                      {u.role.toUpperCase()}
                    </Pill>
                    <TextButton
                      tone="danger"
                      disabled={u.isSelf}
                      onClick={() =>
                        void act(() => api.removeUser(u.id), 'Access could not be removed.')
                      }
                    >
                      Remove
                    </TextButton>
                  </div>
                </Row>
              );
            })}
          </Card>

          {data.invitations.length ? (
            <Card>
              <CardHead
                title="Invited, not yet signed in"
                right={<Mono size={10.5}>{`${data.invitations.length} PENDING`}</Mono>}
              />
              {data.invitations.map(i => (
                <Row key={i.id}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                    <div style={{ fontSize: 14.5, color: c.ink, overflowWrap: 'anywhere' }}>{i.email}</div>
                    <Mono size={10}>EXPIRES {formatDate(i.expiresAt).toUpperCase()}</Mono>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <Pill fg={roleTone(i.role).fg} bg={roleTone(i.role).bg}>
                      {i.role.toUpperCase()}
                    </Pill>
                    <TextButton
                      tone="danger"
                      onClick={() =>
                        void act(() => api.revokeInvitation(i.id), 'The invitation could not be cancelled.')
                      }
                    >
                      Cancel
                    </TextButton>
                  </div>
                </Row>
              ))}
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
